import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid, LineChart, Line
} from 'recharts';
import {
  ArrowLeft, RefreshCw, ExternalLink, IndianRupee, ShoppingCart,
  Boxes, Factory, TrendingUp, CreditCard, Users
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const COLORS = ['#F59E0B', '#22C55E', '#3B82F6', '#8B5CF6', '#EF4444', '#06B6D4', '#F97316', '#EC4899'];
const STATUS_COLORS = {
  'Not Accepted': '#71717A', 'Accepted': '#3B82F6', 'In Production': '#F59E0B',
  'Packing': '#8B5CF6', 'Dispatched': '#22C55E', 'Completed': '#22C55E',
  'Planned': '#71717A', 'QC': '#06B6D4',
};

const PAGE_META = {
  revenue:     { title: 'Revenue Analytics',          icon: IndianRupee, link: '/sales',      linkLabel: 'Go to Sales',       color: 'text-amber-400',  bg: 'bg-amber-500/10'  },
  orders:      { title: 'Active Orders Breakdown',    icon: ShoppingCart, link: '/sales',     linkLabel: 'Go to Sales',       color: 'text-blue-400',   bg: 'bg-blue-500/10'   },
  inventory:   { title: 'Inventory Value Analytics',  icon: Boxes,        link: '/inventory', linkLabel: 'Go to Inventory',   color: 'text-green-400',  bg: 'bg-green-500/10'  },
  production:  { title: 'Production Efficiency',      icon: Factory,      link: '/production',linkLabel: 'Go to Production',  color: 'text-purple-400', bg: 'bg-purple-500/10' },
  receivables: { title: 'Outstanding Receivables',    icon: TrendingUp,   link: '/debtors',   linkLabel: 'View Debtors',      color: 'text-red-400',    bg: 'bg-red-500/10'    },
  payables:    { title: 'Purchase Payables',          icon: CreditCard,   link: '/purchases', linkLabel: 'Go to Purchases',   color: 'text-orange-400', bg: 'bg-orange-500/10' },
  clients:     { title: 'Client Portfolio',           icon: Users,        link: '/debtors',   linkLabel: 'View Clients',      color: 'text-cyan-400',   bg: 'bg-cyan-500/10'   },
};

function TT(props) {
  return <Tooltip contentStyle={{ background: '#18181B', border: '1px solid #3F3F46', borderRadius: '8px', color: '#FAFAFA', fontSize: '11px' }} {...props} />;
}

function StatChip({ label, value, color = 'text-zinc-100', sub, large }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 flex-1 min-w-0">
      <div className="text-zinc-500 text-xs mb-1">{label}</div>
      <div className={`font-mono font-bold ${large ? 'text-2xl' : 'text-lg'} ${color} truncate`}>{value}</div>
      {sub && <div className="text-zinc-600 text-xs mt-0.5">{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return <h3 className="font-heading font-semibold text-zinc-300 text-xs uppercase tracking-wider mb-3">{children}</h3>;
}

// ─── Analytics Views ──────────────────────────────────────────────────────────

function RevenueView({ data }) {
  const [mode, setMode] = React.useState('booked');  // 'booked' or 'dispatched'
  const isBooked = mode === 'booked';
  const totalRev = isBooked ? (data.booked_revenue || 0) : (data.dispatched_revenue || 0);
  const gst = isBooked ? (data.booked_gst || 0) : (data.gst_revenue || 0);
  const nonGst = isBooked ? (data.booked_non_gst || 0) : (data.non_gst_revenue || 0);
  const orderCount = isBooked ? (data.total_orders || 0) : (data.dispatched_count || 0);
  const avgValue = isBooked ? (data.avg_order_value || 0) : (data.avg_dispatched_value || 0);
  const monthlyKey = isBooked ? { gst: 'booked_gst', non_gst: 'booked_non_gst', total: 'booked' }
                              : { gst: 'gst', non_gst: 'non_gst', total: 'dispatched' };

  return (
    <div className="space-y-6">
      {/* Mode toggle — Booked = pipeline, Dispatched = realised */}
      <div className="flex gap-2">
        <button
          onClick={() => setMode('booked')}
          data-testid="rev-mode-booked"
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${isBooked ? 'bg-amber-500 text-black border-amber-500' : 'bg-zinc-900 text-zinc-400 border-zinc-700 hover:border-amber-500/40'}`}>
          Booked (All Orders)
        </button>
        <button
          onClick={() => setMode('dispatched')}
          data-testid="rev-mode-dispatched"
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${!isBooked ? 'bg-green-500 text-black border-green-500' : 'bg-zinc-900 text-zinc-400 border-zinc-700 hover:border-green-500/40'}`}>
          Dispatched (Realised)
        </button>
        <span className="ml-auto text-[11px] text-zinc-500 self-center">
          {isBooked ? '₹ value of every order regardless of status' : '₹ realised once shipped'}
        </span>
      </div>

      {/* Top stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatChip label={isBooked ? 'Total Booked' : 'Total Dispatched'} value={fmt(totalRev)}
          color={isBooked ? 'text-amber-400' : 'text-green-400'} large
          sub={`${orderCount} ${isBooked ? 'orders' : 'shipped'}`} />
        <StatChip label="GST · Tirth Industries" value={fmt(gst)} color="text-blue-400"
          sub={totalRev > 0 ? `${((gst / totalRev) * 100).toFixed(1)}% of total` : '—'} />
        <StatChip label="Non-GST · Tirth Enterprise" value={fmt(nonGst)} color="text-purple-400"
          sub={totalRev > 0 ? `${((nonGst / totalRev) * 100).toFixed(1)}% of total` : '—'} />
        <StatChip label="Avg Order Value" value={fmt(avgValue)} color="text-cyan-400"
          sub={`across ${orderCount} ${isBooked ? 'orders' : 'shipped'}`} />
      </div>

      {/* Realisation strip — only useful in Booked mode */}
      {isBooked && (data.booked_revenue || 0) > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="realisation-strip">
          <div className="flex items-center justify-between text-[11px] text-zinc-400 mb-1.5">
            <span>Realisation funnel: Booked → Dispatched → Paid</span>
            <span className="font-mono">
              {(((data.dispatched_revenue || 0) / data.booked_revenue) * 100).toFixed(1)}% shipped
              {' · '}
              {data.dispatched_revenue ? `${(((data.paid_amount || 0) / data.dispatched_revenue) * 100).toFixed(1)}% paid` : '0% paid'}
            </span>
          </div>
          <div className="h-2 bg-zinc-950 rounded-full overflow-hidden flex">
            <div className="bg-green-500" style={{ width: `${Math.min(100, ((data.paid_amount || 0) / data.booked_revenue) * 100)}%` }}></div>
            <div className="bg-amber-500" style={{ width: `${Math.min(100, ((data.dispatched_revenue || 0) - (data.paid_amount || 0)) / data.booked_revenue * 100)}%` }}></div>
            <div className="bg-zinc-700" style={{ width: `${Math.min(100, ((data.booked_revenue || 0) - (data.dispatched_revenue || 0)) / data.booked_revenue * 100)}%` }}></div>
          </div>
          <div className="flex justify-between text-[10px] mt-1.5 text-zinc-500">
            <span><span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-1"></span>Paid {fmt(data.paid_amount || 0)}</span>
            <span><span className="inline-block w-2 h-2 bg-amber-500 rounded-full mr-1"></span>Shipped unpaid {fmt(data.unpaid_amount || 0)}</span>
            <span><span className="inline-block w-2 h-2 bg-zinc-700 rounded-full mr-1"></span>Pipeline {fmt((data.booked_revenue || 0) - (data.dispatched_revenue || 0))}</span>
          </div>
        </div>
      )}

      {/* Pipeline funnel — counts + value at every status stage */}
      {(data.funnel || []).length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5" data-testid="pipeline-funnel">
          <SectionTitle>Pipeline Funnel</SectionTitle>
          <div className="grid grid-cols-5 gap-2">
            {(data.funnel || []).map((s, i) => (
              <div key={i} className={`p-3 rounded-lg border ${s.status === 'Dispatched' ? 'bg-green-500/10 border-green-500/30' : 'bg-zinc-800/60 border-zinc-700'}`}>
                <div className="text-[10px] text-zinc-400 uppercase tracking-wider mb-1">{s.status}</div>
                <div className="text-zinc-50 font-mono text-sm font-semibold">{s.count}</div>
                <div className="text-zinc-500 text-[10px] mt-0.5">{fmt(s.value)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Monthly {isBooked ? 'Booked' : 'Dispatched'} Revenue — GST vs Non-GST (Last 6 Months)</SectionTitle>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.monthly_breakdown}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="month" tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
              <TT formatter={(v, n) => [fmt(v), n.includes('gst') && !n.startsWith('non') ? 'GST' : n.includes('non') ? 'Non-GST' : n]} />
              <Bar dataKey={monthlyKey.gst} stackId="a" fill="#3B82F6" name="GST" />
              <Bar dataKey={monthlyKey.non_gst} stackId="a" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Non-GST" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Revenue Split</SectionTitle>
          {(gst + nonGst) > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={[{ name: 'GST', value: gst }, { name: 'Non-GST', value: nonGst }].filter(d => d.value > 0)}
                  cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={4} dataKey="value">
                  {[0, 1].map(i => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: '11px', color: '#71717A' }} />
                <TT formatter={v => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-zinc-600 text-xs py-8">No {mode} revenue yet</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Top Clients by Revenue</SectionTitle>
          <div className="space-y-2">
            {(data.top_clients || []).map((c, i) => {
              const rev = isBooked ? (c.revenue || 0) : (c.dispatched_revenue || 0);
              const orders = isBooked ? (c.orders || 0) : (c.dispatched_orders || 0);
              return (
                <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-800/60 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-zinc-700 text-xs w-5 font-mono">{i + 1}.</span>
                    <span className="text-zinc-200 text-sm">{c.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-amber-400 text-sm font-mono font-semibold">{fmt(rev)}</div>
                    <div className="text-zinc-600 text-xs">{orders} orders</div>
                  </div>
                </div>
              );
            })}
            {(data.top_clients || []).length === 0 && (
              <div className="text-zinc-600 text-xs py-4 text-center">No client data yet</div>
            )}
          </div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Top Products by Revenue</SectionTitle>
          <div className="space-y-2">
            {(data.top_products || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-800/60 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="text-zinc-700 text-xs w-5 font-mono">{i + 1}.</span>
                  <span className="text-zinc-200 text-sm">{p.name}</span>
                </div>
                <div className="text-right">
                  <div className="text-green-400 text-sm font-mono font-semibold">{fmt(p.revenue)}</div>
                  <div className="text-zinc-600 text-xs">{(p.units || 0).toLocaleString()} units{p.orders ? ` · ${p.orders} orders` : ''}</div>
                </div>
              </div>
            ))}
            {(data.top_products || []).length === 0 && (
              <div className="text-zinc-600 text-xs py-4 text-center">No product data yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function OrdersView({ data }) {
  // Relabel count→"orders" so Recharts <Legend> shows each slice's status name
  // instead of repeating the word "count" for every entry.
  const pieData = (data.by_status || []).filter(d => d.count > 0).map(d => ({ name: d.status, orders: d.count }));
  const activeCount = data.active_count ?? (data.orders || []).length;
  return (
    <div className="space-y-6">
      <div className="flex gap-3 flex-wrap">
        {(data.by_status || []).map(s => (
          <div key={s.status} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 flex-1 min-w-[120px]">
            <div className="text-zinc-500 text-xs mb-1">{s.status}</div>
            <div className="font-mono font-bold text-2xl" style={{ color: STATUS_COLORS[s.status] }}>{s.count}</div>
            <div className="text-zinc-600 text-xs mt-0.5">{fmt(s.value)}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Status Distribution</SectionTitle>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="orders" nameKey="name">
                {pieData.map((d, i) => <Cell key={i} fill={STATUS_COLORS[d.name] || COLORS[i]} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: '11px', color: '#71717A' }} />
              <TT />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Active Orders ({activeCount})</SectionTitle>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {(data.orders || []).map(o => (
              <div key={o.id} className="flex items-center justify-between py-2 border-b border-zinc-800/60 last:border-0">
                <div className="min-w-0 pr-3">
                  <div className="text-zinc-200 text-sm font-medium truncate">{o.client}</div>
                  <div className="text-zinc-500 text-xs truncate">{o.voucher_no ? <span className="font-mono text-amber-400/80 mr-1.5">{o.voucher_no}</span> : null}{o.product_name}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-amber-400 text-sm font-mono">{fmt(o.total_amount)}</div>
                  <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${STATUS_COLORS[o.status]}20`, color: STATUS_COLORS[o.status] }}>{o.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function InventoryView({ data }) {
  const topLots = (data.lots || []).slice(0, 8);
  return (
    <div className="space-y-6">
      <div className="flex gap-3">
        <StatChip label="Total Inventory Value" value={fmt(data.total_value)} color="text-green-400" large />
        <StatChip label="Low Stock Items" value={data.low_stock?.length || 0} color="text-yellow-400" sub="needs reorder" />
        <StatChip label="Active Lots" value={data.lots?.length || 0} color="text-blue-400" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Top Lots by Value</SectionTitle>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={topLots} layout="vertical">
              <XAxis type="number" tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="material" tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} width={120} />
              <TT formatter={v => fmt(v)} />
              <Bar dataKey="value" fill="#22C55E" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Low Stock Alerts</SectionTitle>
          {(data.low_stock || []).length === 0 ? (
            <div className="text-zinc-500 text-sm">All stock healthy</div>
          ) : (data.low_stock || []).map((m, i) => (
            <div key={i} className="flex items-center justify-between p-2.5 bg-red-900/15 border border-red-800/40 rounded-xl mb-2">
              <span className="text-red-300 text-sm font-medium">{m.name}</span>
              <span className="text-red-400 font-mono text-xs">{m.current_stock} {m.unit}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-800">
          <SectionTitle>All Lots</SectionTitle>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-800">
            {['Material', 'Qty Remaining', 'Cost/Unit', 'Total Value'].map(h => (
              <th key={h} className="text-left text-zinc-600 text-xs font-medium px-5 py-3">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {(data.lots || []).map((l, i) => (
              <tr key={i} className="border-b border-zinc-800/40 hover:bg-zinc-800/20">
                <td className="px-5 py-3 text-zinc-200 font-medium">{l.material}</td>
                <td className="px-5 py-3 font-mono text-zinc-400">{(l.qty || 0).toLocaleString()}</td>
                <td className="px-5 py-3 font-mono text-zinc-400">{fmt(l.cost)}</td>
                <td className="px-5 py-3 font-mono text-amber-400 font-semibold">{fmt(l.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProductionView({ data }) {
  return (
    <div className="space-y-6">
      <div className="flex gap-3">
        {(data.by_status || []).map(s => (
          <div key={s.status} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 flex-1">
            <div className="text-zinc-500 text-xs mb-1">{s.status}</div>
            <div className="font-mono font-bold text-2xl" style={{ color: STATUS_COLORS[s.status] }}>{s.count}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Efficiency by Product</SectionTitle>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_product || []} layout="vertical">
              <XAxis type="number" tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} domain={[0, 100]} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} width={110} />
              <TT formatter={v => [`${v}%`, 'Efficiency']} />
              <Bar dataKey="efficiency" fill="#F59E0B" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Product Summary</SectionTitle>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {(data.by_product || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-800/60 last:border-0">
                <div>
                  <div className="text-zinc-200 text-sm font-medium">{p.name}</div>
                  <div className="text-zinc-500 text-xs">{p.total} batches · {p.units?.toLocaleString()} units</div>
                </div>
                <div className="font-mono text-amber-400 text-sm font-semibold">{p.efficiency}%</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-800"><SectionTitle>Recent Batches</SectionTitle></div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-800">
            {['Batch ID', 'Product', 'Quantity', 'Status'].map(h => (
              <th key={h} className="text-left text-zinc-600 text-xs font-medium px-5 py-3">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {(data.batches || []).slice(0, 15).map(b => (
              <tr key={b.id} className="border-b border-zinc-800/40 hover:bg-zinc-800/20">
                <td className="px-5 py-3 font-mono text-amber-400 text-xs">{b.id}</td>
                <td className="px-5 py-3 text-zinc-200">{b.product_name}</td>
                <td className="px-5 py-3 font-mono text-zinc-400">{b.quantity} {b.unit || 'kg'}</td>
                <td className="px-5 py-3">
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${STATUS_COLORS[b.status]}20`, color: STATUS_COLORS[b.status] }}>{b.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReceivablesView({ data }) {
  const agingChart = Object.entries(data.aging || {}).map(([bucket, v]) => ({ bucket, ...v }));
  const agingColor = { '0-30': '#22C55E', '31-60': '#F59E0B', '61-90': '#F97316', '90+': '#EF4444' };
  return (
    <div className="space-y-6">
      <div className="flex gap-3">
        <StatChip label="Total Outstanding" value={fmt(data.total_outstanding)} color="text-red-400" large />
        {agingChart.map(({ bucket, count, amount }) => (
          <StatChip key={bucket} label={`${bucket} days`} value={fmt(amount)} color={amount > 0 ? 'text-zinc-100' : 'text-zinc-500'} sub={`${count} order(s)`} />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Aging Analysis</SectionTitle>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={agingChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="bucket" tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
              <TT formatter={v => fmt(v)} />
              <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                {agingChart.map(({ bucket }, i) => <Cell key={i} fill={agingColor[bucket]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>By Client (Outstanding)</SectionTitle>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {(data.by_client || []).map((c, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-800/60 last:border-0">
                <div>
                  <div className="text-zinc-200 text-sm font-medium">{c.client}</div>
                  <div className="text-zinc-500 text-xs">{c.orders} order(s)</div>
                </div>
                <div className="text-red-400 font-mono text-sm font-semibold">{fmt(c.outstanding)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-800"><SectionTitle>Unpaid Orders</SectionTitle></div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-800">
            {['Order ID', 'Client', 'Product', 'Total', 'Paid', 'Outstanding', 'Status'].map(h => (
              <th key={h} className="text-left text-zinc-600 text-xs font-medium px-5 py-3">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {(data.orders || []).map(o => {
              const outstanding = parseFloat(o.total_amount || 0) - parseFloat(o.paid_amount || 0);
              return (
                <tr key={o.id} className="border-b border-zinc-800/40 hover:bg-zinc-800/20">
                  <td className="px-5 py-3 font-mono text-amber-400 text-xs">{o.id}</td>
                  <td className="px-5 py-3 text-zinc-200">{o.client}</td>
                  <td className="px-5 py-3 text-zinc-400">{o.product_name}</td>
                  <td className="px-5 py-3 font-mono text-zinc-300">{fmt(o.total_amount)}</td>
                  <td className="px-5 py-3 font-mono text-green-400">{fmt(o.paid_amount)}</td>
                  <td className="px-5 py-3 font-mono text-red-400 font-semibold">{fmt(outstanding)}</td>
                  <td className="px-5 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${STATUS_COLORS[o.status]}20`, color: STATUS_COLORS[o.status] }}>{o.status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayablesView({ data }) {
  const PAY_STATUS = { 'Paid': '#22C55E', 'Partial': '#F59E0B', 'Unpaid': '#EF4444' };
  return (
    <div className="space-y-6">
      <div className="flex gap-3">
        <StatChip label="Total Payables" value={fmt(data.total_payables)} color="text-red-400" large />
        <StatChip label="Vendors Billed" value={data.by_vendor?.length || 0} color="text-amber-400" />
        <StatChip label="Total Bills" value={data.recent_bills?.length || 0} color="text-blue-400" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Payables by Vendor</SectionTitle>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={(data.by_vendor || []).slice(0, 8)} layout="vertical">
              <XAxis type="number" tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="vendor" tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} width={110} />
              <TT formatter={v => fmt(v)} />
              <Bar dataKey="total" fill="#EF4444" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Payment Status Summary</SectionTitle>
          {(data.by_vendor || []).map((v, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-800/60 last:border-0">
              <div className="text-zinc-200 text-sm">{v.vendor}</div>
              <div className="text-right">
                <div className="text-amber-400 font-mono text-sm font-semibold">{fmt(v.total)}</div>
                <div className="text-zinc-600 text-xs">{v.bills} bill(s)</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-800"><SectionTitle>All Purchase Bills</SectionTitle></div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-800">
            {['Bill ID', 'Vendor', 'Invoice No.', 'Date', 'Product', 'Total', 'Paid', 'Status'].map(h => (
              <th key={h} className="text-left text-zinc-600 text-xs font-medium px-5 py-3">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {(data.recent_bills || []).map(b => (
              <tr key={b.id} className="border-b border-zinc-800/40 hover:bg-zinc-800/20">
                <td className="px-5 py-3 font-mono text-amber-400 text-xs">{b.id}</td>
                <td className="px-5 py-3 text-zinc-200 font-medium">{b.vendor_name}</td>
                <td className="px-5 py-3 font-mono text-zinc-400 text-xs">{b.invoice_number || '—'}</td>
                <td className="px-5 py-3 text-zinc-500 text-xs">{b.invoice_date || '—'}</td>
                <td className="px-5 py-3 text-zinc-400">{b.product_name || '—'}</td>
                <td className="px-5 py-3 font-mono text-amber-400 font-semibold">{fmt(b.total_amount)}</td>
                <td className="px-5 py-3 font-mono text-green-400">{fmt(b.paid_amount)}</td>
                <td className="px-5 py-3">
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${PAY_STATUS[b.payment_status] || '#71717A'}20`, color: PAY_STATUS[b.payment_status] || '#71717A' }}>{b.payment_status || 'Unpaid'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ClientsView({ data }) {
  const typeChart = [
    { name: 'Debtors', value: data.debtors || 0 },
    { name: 'Creditors', value: data.creditors || 0 },
    { name: 'Both', value: Math.max(0, (data.clients?.length || 0) - (data.debtors || 0) - (data.creditors || 0)) },
  ].filter(d => d.value > 0);
  return (
    <div className="space-y-6">
      <div className="flex gap-3">
        <StatChip label="Total Clients" value={data.clients?.length || 0} color="text-amber-400" large />
        <StatChip label="Sundry Debtors" value={data.debtors || 0} color="text-blue-400" />
        <StatChip label="Sundry Creditors" value={data.creditors || 0} color="text-purple-400" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Client Type Distribution</SectionTitle>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={typeChart} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value">
                {typeChart.map((d, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: '11px', color: '#71717A' }} />
              <TT />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <SectionTitle>Top Debtors by Outstanding</SectionTitle>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {(data.top_debtors || []).map((c, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-800/60 last:border-0">
                <div>
                  <div className="text-zinc-200 text-sm font-medium">{c.name}</div>
                  <div className="text-zinc-500 text-xs">{c.city || c.state}</div>
                </div>
                <div className={`font-mono text-sm font-semibold ${c.outstanding_balance > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {fmt(c.outstanding_balance)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-800"><SectionTitle>All Clients</SectionTitle></div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-800">
            {['Name', 'Type', 'City', 'Payment Terms', 'Credit Limit', 'Outstanding'].map(h => (
              <th key={h} className="text-left text-zinc-600 text-xs font-medium px-5 py-3">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {(data.clients || []).map(c => (
              <tr key={c.id} className="border-b border-zinc-800/40 hover:bg-zinc-800/20">
                <td className="px-5 py-3 text-zinc-200 font-medium">{c.name}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${c.type === 'Debtor' ? 'bg-blue-900/40 text-blue-300' : c.type === 'Creditor' ? 'bg-purple-900/40 text-purple-300' : 'bg-amber-900/40 text-amber-300'}`}>{c.type}</span>
                </td>
                <td className="px-5 py-3 text-zinc-500">{c.city || '—'}</td>
                <td className="px-5 py-3 text-zinc-400">{c.payment_terms}</td>
                <td className="px-5 py-3 font-mono text-zinc-400">{c.credit_limit ? fmt(c.credit_limit) : '—'}</td>
                <td className="px-5 py-3 font-mono font-semibold" style={{ color: c.outstanding_balance > 0 ? '#F87171' : '#4ADE80' }}>{fmt(c.outstanding_balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Analytics Page ──────────────────────────────────────────────────────

export default function AnalyticsPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const meta = PAGE_META[type] || { title: 'Analytics', link: '/dashboard', linkLabel: 'Back', color: 'text-zinc-400', bg: 'bg-zinc-800', icon: BarChart };
  const Icon = meta.icon || (() => null);

  useEffect(() => {
    setLoading(true);
    setData(null);
    axios.get(`${API}/dashboard/drilldown/${type}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [type]);

  const renderView = () => {
    if (!data) return null;
    switch (type) {
      case 'revenue':     return <RevenueView data={data} />;
      case 'orders':      return <OrdersView data={data} />;
      case 'inventory':   return <InventoryView data={data} />;
      case 'production':  return <ProductionView data={data} />;
      case 'receivables': return <ReceivablesView data={data} />;
      case 'payables':    return <PayablesView data={data} />;
      case 'clients':     return <ClientsView data={data} />;
      default: return <div className="text-zinc-500">Unknown analytics type</div>;
    }
  };

  return (
    <div className="space-y-6" data-testid={`analytics-page-${type}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            data-testid="analytics-back-btn"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-1.5 text-zinc-400 hover:text-zinc-100 text-sm transition-colors group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            Dashboard
          </button>
          <div className="w-px h-5 bg-zinc-800" />
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl ${meta.bg} flex items-center justify-center`}>
              <Icon size={17} className={meta.color} />
            </div>
            <div>
              <h1 className="font-heading text-xl font-semibold text-zinc-50">{meta.title}</h1>
              <p className="text-zinc-500 text-xs mt-0.5">Live data from Tirth Industries ERP</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setLoading(true); setData(null); axios.get(`${API}/dashboard/drilldown/${type}`, { withCredentials: true }).then(r => setData(r.data)).finally(() => setLoading(false)); }}
            className="p-2 rounded-xl text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
            title="Refresh"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            data-testid="analytics-go-to-module-btn"
            onClick={() => navigate(meta.link)}
            className="flex items-center gap-1.5 text-xs text-amber-400 border border-amber-500/30 px-3 py-1.5 rounded-xl hover:bg-amber-500/10 transition-colors"
          >
            {meta.linkLabel} <ExternalLink size={11} />
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw size={24} className="animate-spin text-zinc-600" />
        </div>
      ) : !data ? (
        <div className="text-center text-zinc-500 py-16">No data available</div>
      ) : renderView()}
    </div>
  );
}
